/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unused-imports/no-unused-imports */
import { List, useModalForm, useTable } from "@refinedev/antd";
import {
  BaseRecord,
  CrudFilters,
  IResourceComponentsProps,
  getDefaultFilter,
} from "@refinedev/core";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DeleteOutlined, IdcardOutlined } from "@ant-design/icons";
import { Badge, Button, Table } from "antd";
import { ColumnType } from "antd/es/table";

import { useModalForm as useModalFormAntd } from "sunflower-antd";
import { NewCompanyModalCreate } from "../../components/modal/admin/addCompany";
import { EditCompany } from "../../components/modal/admin/editCompany";
import { useDeleteCompany } from "../../utils/hooks/useDeleteCompany.hook";
import { useIsAdminRole } from "../../utils/hooks/useIdentityAdmin.hook";
import { useNotificationOkCancel } from "../../utils/hooks/useNotificationOkCancel.hook";
import { VerificationStatus } from "../../utils/hooks/useWorldCheckVerification.hook";
import { ButtonListHeader } from "../components/buttonsList";
import { confirmModalDeleteProps } from "../components/constants";
import { Row } from "../components/interface";
import { TableForm } from "../crypto-withdrawal/tableForm";
import {
  verificationStatus,
  verificationStatusColors,
} from "../customers/worldCheckStatus.utils";
import "../styles/index.css";
import { StatusFilter } from "./StatusFilter";

export const CompanyList: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState<number>();
  const [deletingId, setDeletingId] = useState<number>();
  const isAdminRole = useIsAdminRole();
  const {
    tableProps,
    searchFormProps,
    filters,
    setCurrent,
    tableQueryResult: { refetch: refetchList },
  } = useTable<Row>({
    syncWithLocation: true,
    pagination: {
      pageSize: 10,
    },
    permanentFilter: [
      {
        field: "_isProvider",
        operator: "eq",
        value: true,
      },
    ],
    onSearch: (params: any) => {
      const { searchValue, isDefault } = params;
      const filters: CrudFilters = [
        {
          field: "",
          operator: "contains",
          value: searchValue,
        },
        {
          field: "_isDefault",
          operator: "eq",
          value: isDefault,
        },
        {
          field: "_isProvider",
          operator: "eq",
          value: true,
        },
      ];
      return filters;
    },
  });

  const { deleteCompany } = useDeleteCompany();

  const reloadListAfterDelete = useCallback(async () => {
    const result = await refetchList();
    const pagination = tableProps.pagination;
    if (!pagination || typeof pagination === "boolean") return;

    const responseData: any = result?.data;
    const pageSize = Number(pagination.pageSize || 10);
    const currentPage = Number(pagination.current || 1);
    const responseTotal = Number(
      responseData?.total ??
      responseData?.totalCount ??
      responseData?.data?.total ??
      responseData?.data?.totalCount
    );

    const total = Number.isNaN(responseTotal)
      ? Number(pagination.total || 0)
      : responseTotal;
    const targetPage = Math.max(1, Math.ceil(total / pageSize));

    if (currentPage > targetPage) {
      if (setCurrent) {
        setCurrent(targetPage);
      } else {
        pagination.onChange?.(targetPage, pageSize);
      }
    }
  }, [refetchList, setCurrent, tableProps.pagination]);

  const onSearchDefaultCompany = () => {
    searchFormProps?.form?.submit();
  };

  const onOkDelete = useCallback(async () => {
    if (deletingId !== undefined) {
      setDeletingId(undefined);
      await deleteCompany(deletingId);
      await reloadListAfterDelete();
    }
  }, [deletingId, deleteCompany, reloadListAfterDelete]);

  const onCancelDelete = useCallback(() => {
    setDeletingId(undefined);
  }, []);

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalDeleteProps,
    onOk: onOkDelete,
    onCancel: onCancelDelete,
  });

  const {
    modalProps: createCompanyModalProps,
    formProps: createCompanyFormProps,
    show: createCompanyModalShow,
    formLoading: createCompanyFormLoading,
    close: createCompanyModalClose,
    onFinish: createCompanyOnFinish,
  } = useModalForm({
    action: "create",
  });

  const openCreateCompanyModal = () => {
    createCompanyModalShow();
  };

  const {
    close: editModalClose,
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editModalShow,
  } = useModalFormAntd({});

  const handleClickOnCell = (record: BaseRecord) => {
    return {
      onClick: () => {
        if (record.id && isAdminRole) {
          navigate(`/company/${record.id}`);
        }
      },
    };
  };

  const defaultFilterValue = getDefaultFilter("_isDefault", filters);

  useEffect(() => {
    if (deletingId !== undefined) {
      openNotification();
    }
  }, [deletingId, openNotification]);

  const onClickDelete = (id: number) => () => {
    setDeletingId(id);
  };

  const columns: ColumnType<Row>[] = [
    {
      dataIndex: "id",
      title: (
        <span className={"column-header"}>
          <IdcardOutlined />
          &nbsp;ID
        </span>
      ),
      sorter: true,
      onCell: handleClickOnCell,
    },
    {
      dataIndex: "name",
      title: (
        <span className={"column-header"}>
          <IdcardOutlined />
          &nbsp;Name
        </span>
      ),
      sorter: true,
      onCell: handleClickOnCell,
    },
    {
      dataIndex: ["worldCheckVerification", "status"],
      title: "WorldCheck Status",
      onCell: handleClickOnCell,
      render: (value: VerificationStatus) => {
        return (
          <Badge
            color={verificationStatusColors[value]}
            text={verificationStatus[value]}
          />
        );
      },
    },
    {
      dataIndex: "tinVatUid",
      title: "VAT/TIN/UID",
      onCell: handleClickOnCell,
    },
    {
      dataIndex: "address1",
      title: (
        <span className={"column-header"}>
          <IdcardOutlined />
          &nbsp;Address 1
        </span>
      ),
      onCell: handleClickOnCell,
    },
    {
      dataIndex: "address2",
      title: (
        <span className={"column-header"}>
          <IdcardOutlined />
          &nbsp;Address 2
        </span>
      ),
      onCell: handleClickOnCell,
    },
    {
      dataIndex: "country",
      title: (
        <span className={"column-header"}>
          <IdcardOutlined />
          &nbsp;Country
        </span>
      ),
      onCell: handleClickOnCell,
    },
    {
      dataIndex: "regNo",
      title: (
        <span className={"column-header"}>
          <IdcardOutlined />
          &nbsp;Reg #
        </span>
      ),
      onCell: handleClickOnCell,
    },
    {
      dataIndex: "phoneNumber1",
      title: (
        <span className={"column-header"}>
          <IdcardOutlined />
          &nbsp;Tel
        </span>
      ),
      onCell: handleClickOnCell,
    },
    {
      render: (value, data) => {
        const canDelete = data.canBeDeleted;
        return (
          isAdminRole && (
            <Button
              className={"list-button list-button--delete"}
              icon={<DeleteOutlined style={{ fontSize: "19px" }} />}
              size="small"
              onClick={onClickDelete(data.id)}
            />
          )
        );
      },
    },
  ];

  return (
    <>
      <List
        title="Provider Directory"
        headerButtons={null}
      >
        <TableForm
          {...searchFormProps}
          initialValues={{
            searchValue: getDefaultFilter("", filters),
          }}
          onSubmit={searchFormProps.form?.submit}
          searchPlaceholder={
            "Search provider by name, TIN, Reg No, or phone number"
          }
          showDateFilter={false}
          headerButtons={
            isAdminRole ? ButtonListHeader(openCreateCompanyModal) : null
          }
        />
        <StatusFilter {...searchFormProps} defaultValue={defaultFilterValue} />
        <Table
          {...tableProps}
          rowKey="id"
          columns={columns}
          dataSource={tableProps.dataSource}
        />
      </List>
      {contextHolder}
      <NewCompanyModalCreate
        close={createCompanyModalClose}
        formProps={createCompanyFormProps}
        modalProps={createCompanyModalProps}
        formIsLoading={createCompanyFormLoading}
        onFinish={createCompanyOnFinish}
      // refetchList={refetchList}
      />
      <EditCompany
        editingId={editingId}
        changeEditingId={setEditingId}
        show={editModalShow}
        formProps={editFormProps}
        modalProps={editModalProps}
        refetchList={refetchList}
        closeModal={editModalClose}
      />
    </>
  );
};
